import React, { useCallback, useEffect, useState } from 'react';
import { Clock, RotateCcw, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import api from '../../lib/api';

const HOURS = Array.from({ length: 24 }, (_, h) => h);
const ZONES = ['UTC', 'Australia/Sydney', 'Australia/Melbourne', 'Australia/Brisbane', 'Australia/Adelaide', 'Australia/Perth', 'Pacific/Auckland', 'Asia/Singapore', 'Asia/Tokyo', 'Asia/Kolkata', 'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'America/Toronto'];
const hh = (h) => `${String(h).padStart(2, '0')}:00`;

// Super admin: per-organisation server warm-up hour (in the org's timezone). Rows fall back to the global default.
export default function WarmScheduleTable() {
  const [d, setD] = useState(null);
  const [saving, setSaving] = useState(null);
  const load = useCallback(() => api.get('/health-cache/warm-schedule').then((r) => setD(r.data)).catch(() => setD({ orgs: [] })), []);
  useEffect(() => { load(); }, [load]);
  const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const zones = ZONES.includes(browserTz) ? ZONES : [browserTz, ...ZONES];

  const save = async (org, patch) => {
    setSaving(org.organization_id);
    try {
      const r = await api.put(`/health-cache/warm-schedule/${org.organization_id}`, { hour: patch.hour ?? org.hour, tz: patch.tz ?? org.tz });
      setD(r.data);
      toast.success(`${org.name}: server warm-up at ${hh(patch.hour ?? org.hour)} ${patch.tz ?? org.tz}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save schedule'); }
    finally { setSaving(null); }
  };
  const reset = async (org) => {
    setSaving(org.organization_id);
    try { const r = await api.delete(`/health-cache/warm-schedule/${org.organization_id}`); setD(r.data); toast.success(`${org.name}: back to the default hour`); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not reset'); }
    finally { setSaving(null); }
  };

  if (!d) return null;
  return (
    <div className="rounded-lg border border-slate-200" data-testid="warm-schedule-table">
      <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2">
        <span className="flex items-center gap-1.5 text-xs font-semibold text-slate-800"><Clock className="h-3.5 w-3.5 text-indigo-600" /> Warm-up hour per organisation</span>
        <span className="text-[11px] text-slate-500" data-testid="warm-schedule-default">Default {hh(d.default_hour_utc)} on each org's home timezone (UTC if unset) · override here to run an hour or two before the first browser warm-up</span>
      </div>
      <table className="w-full text-xs">
        <thead><tr className="text-left text-[10px] uppercase tracking-[0.12em] text-slate-500"><th className="px-3 py-1.5">Organisation</th><th className="py-1.5 pr-2">Hour</th><th className="py-1.5 pr-2">Timezone</th><th className="py-1.5 pr-2">Next run</th><th className="py-1.5 pr-3 text-right" /></tr></thead>
        <tbody>
          {d.orgs.map((o) => {
            const busy = saving === o.organization_id;
            const next = new Date(o.next_run_utc);
            return (
              <tr key={o.organization_id} className="border-t border-slate-100" data-testid={`warm-schedule-row-${o.organization_id}`} data-source={o.source}>
                <td className="px-3 py-1.5 font-medium text-slate-800">{o.name}{o.source === 'override' ? <span className="ml-1.5 rounded bg-indigo-50 px-1.5 py-0.5 text-[10px] font-bold text-indigo-700">custom</span> : o.org_tz ? <span className="ml-1.5 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600" title="Default hour on the organisation's home timezone">org clock</span> : null}</td>
                <td className="py-1.5 pr-2">
                  <select value={o.hour} disabled={busy} onChange={(e) => save(o, { hour: Number(e.target.value) })} className="rounded border border-slate-200 bg-white px-1.5 py-0.5 text-xs" data-testid={`warm-schedule-hour-${o.organization_id}`}>
                    {HOURS.map((h) => <option key={h} value={h}>{hh(h)}</option>)}
                  </select>
                </td>
                <td className="py-1.5 pr-2">
                  <select value={o.tz} disabled={busy} onChange={(e) => save(o, { tz: e.target.value })} className="max-w-[180px] rounded border border-slate-200 bg-white px-1.5 py-0.5 text-xs" data-testid={`warm-schedule-tz-${o.organization_id}`}>
                    {(zones.includes(o.tz) ? zones : [o.tz, ...zones]).map((z) => <option key={z} value={z}>{z}</option>)}
                  </select>
                </td>
                <td className="py-1.5 pr-2 text-slate-600 whitespace-nowrap" data-testid={`warm-schedule-next-${o.organization_id}`} title={next.toISOString()}>
                  {next.toLocaleString(undefined, { weekday: 'short', hour: '2-digit', minute: '2-digit', timeZone: o.tz })} {o.tz === 'UTC' ? 'UTC' : 'local'} · {next.toLocaleString(undefined, { hour: '2-digit', minute: '2-digit' })} here
                </td>
                <td className="py-1.5 pr-3 text-right">
                  {busy ? <Loader2 className="inline h-3.5 w-3.5 animate-spin text-slate-400" /> : o.source === 'override' && (
                    <button type="button" onClick={() => reset(o)} className={cn('inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] text-slate-500 hover:bg-slate-100 hover:text-slate-800')} data-testid={`warm-schedule-reset-${o.organization_id}`}><RotateCcw className="h-3 w-3" /> Default</button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
